import { useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { BungieError } from '../../lib/bungie';
import { useHub, usePrivate } from '../context';
import {
  capacity, equipItem, equipLoadout, isArmor, isCrafted, isLocked, isMasterwork, isWeapon, loadLoadouts, loadStash, LOST_ITEMS,
  buildRolled, loadRolls, pullFromPostmaster, rollTables, setLock, SUBCLASS_BUCKET, VAULT, type GearStat, type Loadout, type Rolled, type Rolls, type RollTables, type Stash, type StashItem,
} from '../character';
import type { HubChar } from '../hub';
import {
  canWear, exotic, GROUPS, groupSpots, keyOf, maxCache, maxPowerSet, plan, searchOf, SEARCH_HELP, SEARCH_WORDS, SECTIONS, SORTS, spotsOf, TAGS,
  unknownWords,
  type Marks, type Rules, type Spot, type Step, type Tag, type Where,
} from '../items';
import { Message, Pic, SmallButton } from '../ui';
import { useTable } from '../../lib/manifest';
import { EXTENDED_BREAKER, type IconDef, type ItemDef } from '../../lib/tables';
import { godMarkByName, godRolls, type GodMark, type RollTable } from '../godrolls';
import { Compare, GodThumb, Organizer } from './ItemViews';
import { ItemInspect } from './ItemInspect';
import '../itemmanager.css';

/**
 * The Item Manager (in the spirit of DIM, Destiny Item Manager): every character's worn and carried gear and the vault
 * side by side, one row per slot. Drag an item onto a character to move it there, onto the big square to equip it,
 * onto the vault to put it away; or click it for the same choices. The moves the game needs on the way (wearing
 * something else first, making room, the one-exotic rule) are worked out here and run one after another.
 */

export const ITEMS_NOTE = 'What each character wears and carries, and your vault, side by side. Drag an item to move or equip it.';

const LAG = 30_000;   // how long Bungie's profile can lag behind a move
const SIZES: [number, string][] = [[42, 'Small'], [50, 'Medium'], [58, 'Large'], [66, 'Huge']];   // Medium: DIM's default square, Huge: its largest
const SIZE_KEY = 'd2c.im.size';
const HIDE_KEY = 'd2c.im.hidden';
const readSize = () => { try { const n = Number(localStorage.getItem(SIZE_KEY)); return SIZES.some(([s]) => s === n) ? n : 50; } catch { return 50; } };

const SORT_KEY = 'd2c.im.sort';
const VIEW_KEY = 'd2c.im.view2';   // (2: the one-character view became the first choice, 2026-10-25)
type View = 'focus' | 'board' | 'table';
const LAYOUT_KEY = 'd2c.im.layout';
interface Layout { group: string; junkApart: boolean; folded: string[]; dense: boolean }
const LAYOUT0: Layout = { group: 'none', junkApart: true, folded: ['junk'], dense: true };

/** A small per-account map kept in this browser (tags, notes): d2c.<name>.<account>. */
function useStoredMap<V>(name: string, account?: string) {
  const key = `d2c.${name}.${account ?? ''}`;
  const [map, setMap] = useState<Record<string, V>>({});
  useEffect(() => { try { setMap(JSON.parse(localStorage.getItem(key) ?? '{}')); } catch { setMap({}); } }, [key]);
  const set = (id: string, value?: V) => setMap((cur) => {
    const next = { ...cur };
    if (value) next[id] = value; else delete next[id];
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage blocked: it lasts until the page closes */ }
    return next;
  });
  // replace the whole map (from the account's copy on the server), from what is there now
  const all = (fn: (cur: Record<string, V>) => Record<string, V>) => setMap((cur) => {
    const next = fn(cur);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage blocked */ }
    return next;
  });
  return [map, set, all] as const;
}
const useTags = (account?: string) => useStoredMap<Tag>('tags', account);
const useNotes = (account?: string) => useStoredMap<string>('notes', account);

// ---------------- the page ----------------

export default function ItemManager() {
  const h = useHub();
  const nav = useNavigate();
  const { data: fresh, error, again } = usePrivate<Stash>('stash', (t) => loadStash(h.player!, t));
  const kept = useRef<Stash>(undefined);   // the last answer stays on screen while the next is fetched
  if (fresh) kept.current = fresh;
  const stash = fresh ?? kept.current;
  const [spots, setSpots] = useState<Spot[]>();
  const live = useRef<Spot[]>([]);
  const moved = useRef(false);   // after a move, Bungie's next answer can lag behind: keep our own picture until Read again
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<{ text: string; bad?: boolean }>();
  const [drag, setDrag] = useState<string>();
  const [over, setOver] = useState<string>();
  const [menu, setMenu] = useState<{ id: string; x: number; y: number }>();
  const [compare, setCompare] = useState<string>();
  const cardKeys = useRef<(e: KeyboardEvent) => void>(() => undefined);   // the open card's shortcuts (set each render)
  const [search, setSearch] = useState('');
  const [help, setHelp] = useState(false);
  const searchBox = useRef<HTMLInputElement>(null);
  // DIM's shortcut: / jumps to the search box
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === '/' && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) { e.preventDefault(); searchBox.current?.focus(); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);
  useEffect(() => { if (!help) return; const off = () => setHelp(false); window.addEventListener('click', off); return () => window.removeEventListener('click', off); }, [help]);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const damageTypes = useTable<any>('DestinyDamageTypeDefinition');
  const elementIcons = damageTypes && Object.fromEntries(Object.values<any>(damageTypes).map((d) => [d.enumValue, d.displayProperties?.icon ?? '']));
  const [size, setSize] = useState(readSize);
  // the layout choices in the View menu (kept in this browser): grouping, folded groups, junk apart, spacing
  const [layout, setLayout] = useState<Layout>(() => { try { return { ...LAYOUT0, ...JSON.parse(localStorage.getItem(LAYOUT_KEY) ?? '{}') }; } catch { return LAYOUT0; } });
  useEffect(() => { try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout)); } catch { /* storage blocked */ } }, [layout]);
  const [viewOpen, setViewOpen] = useState(false);
  useEffect(() => { if (!viewOpen) return; const off = () => setViewOpen(false); window.addEventListener('click', off); return () => window.removeEventListener('click', off); }, [viewOpen]);
  const narrow = useNarrow();
  const [view, setView] = useState<View>(() => { try { const v = localStorage.getItem(VIEW_KEY); return v === 'table' || v === 'board' ? v : 'focus'; } catch { return 'focus'; } });
  const [chips, setChips] = useState<string[]>([]);   // the quick filters beside the one-character view
  const [subWorn, setSubWorn] = useState<Record<string, string>>({});   // a subclass worn since the last read, per character
  const [fullVaults, setFullVaults] = useState<number[]>([]);   // its vault grids shown in full (else a few rows)
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch { /* storage blocked */ } }, [view]);
  const [sort, setSort] = useState(() => { try { return localStorage.getItem(SORT_KEY) ?? 'rarity'; } catch { return 'rarity'; } });
  useEffect(() => { try { localStorage.setItem(SORT_KEY, sort); } catch { /* storage blocked */ } }, [sort]);
  const [focus, setFocus] = useState<string>();
  const [openVaults, setOpenVaults] = useState<number[]>([]);   // phones: the vault part of each slot folded until tapped
  const [hidden, setHidden] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem(HIDE_KEY) ?? '[]'); } catch { return []; } });
  useEffect(() => { try { localStorage.setItem(HIDE_KEY, JSON.stringify(hidden)); } catch { /* storage blocked */ } }, [hidden]);
  useEffect(() => { try { localStorage.setItem(SIZE_KEY, String(size)); } catch { /* storage blocked */ } }, [size]);
  const queue = useRef(Promise.resolve());
  const waiting = useRef(0);
  const lastMove = useRef(0);
  const later = useRef<number>(undefined);
  const againRef = useRef(again);
  againRef.current = again;
  useEffect(() => () => window.clearTimeout(later.current), []);
  const [tags, setTag, allTags] = useTags(h.player?.membershipId);
  // new items, as DIM marks them: anything not seen on this browser before (the first visit counts everything as seen)
  const seenKey = `d2c.seen.${h.player?.membershipId ?? ''}`;
  const [seen, setSeen] = useState<Set<string>>();
  useEffect(() => {
    if (!spots) return;
    let known: string[] | null = null;
    try { known = JSON.parse(localStorage.getItem(seenKey) ?? 'null'); } catch { /* unreadable: start again */ }
    setSeen(new Set(known ?? spots.map((s) => keyOf(s.item))));
  }, [seenKey, !!spots]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (seen) try { localStorage.setItem(seenKey, JSON.stringify([...seen])); } catch { /* storage blocked */ } }, [seen, seenKey]);
  const isNew = (id: string) => !!seen && !seen.has(id);
  const markSeen = (id: string) => { if (isNew(id)) setSeen((cur) => new Set(cur).add(id)); };
  const clearNew = () => setSeen((cur) => new Set([...(cur ?? []), ...(live.current.map((s) => keyOf(s.item)))]));
  const [notes, setNoteFor, allNotes] = useNotes(h.player?.membershipId);
  const savedLoadouts = useSavedLoadouts(h.player?.membershipId);
  // tags, notes and loadouts follow the account to other devices (the site's /api/store), once the site has storage:
  // the account's copy is merged into this browser's (nothing is lost), then every change is sent a moment later
  const [sync, setSync] = useState<'local' | 'reading' | 'synced'>('local');
  useEffect(() => {
    if (!h.own || !h.player) return;
    let on = true;
    setSync('reading');
    fetch(`/api/store?profile=${h.player.membershipId}`, { credentials: 'same-origin' })
      .then((r) => (r.ok && (r.headers.get('Content-Type') ?? '').includes('json') ? r.json() : null)).catch(() => null)
      .then((doc: { tags?: Record<string, Tag>; notes?: Record<string, string>; loadouts?: SavedLoadout[]; at?: number } | null) => {
        if (!on) return;
        if (!doc) { setSync('local'); return; }
        // the account's copy, once it has one, is the truth (so a tag removed on one device stays removed on the
        // others); a browser's own data is only added the first time, when the account has none yet
        const first = !doc.at;
        allTags((cur) => (first ? { ...cur, ...(doc.tags ?? {}) } : { ...(doc.tags ?? {}) }));
        allNotes((cur) => (first ? { ...cur, ...(doc.notes ?? {}) } : { ...(doc.notes ?? {}) }));
        savedLoadouts.merge(doc.loadouts ?? [], !first);
        setSync('synced');
      });
    return () => { on = false; };
  }, [h.own, h.player?.membershipId]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sync !== 'synced' || !h.player) return;
    const t = window.setTimeout(() => {
      void fetch(`/api/store?profile=${h.player!.membershipId}`, {
        method: 'PUT', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags, notes, loadouts: savedLoadouts.list, at: Date.now() }),
      }).catch(() => undefined);
    }, 1500);
    return () => window.clearTimeout(t);
  }, [sync, tags, notes, savedLoadouts.list]);   // eslint-disable-line react-hooks/exhaustive-deps
  // god rolls: the community list, and every item's perks (a heavier read, asked for once the board is up)
  const [rollTable, setRollTable] = useState<RollTable>();
  useEffect(() => { let on = true; godRolls().then((t) => on && setRollTable(t)).catch(() => undefined); return () => { on = false; }; }, []);
  // every item's roll in one read after the board is up, and the tables to build a card from: a card then opens at
  // once, as DIM's do (before they arrive, a card reads its own roll)
  const { data: rolls } = usePrivate<Rolls>(stash ? 'rolls' : undefined, (t) => loadRolls(h.player!, t));
  const plugged = rolls?.plugged;
  const [tablesForRolls, setTablesForRolls] = useState<RollTables>();
  useEffect(() => { let on = true; rollTables().then((t) => on && setTablesForRolls(t)).catch(() => undefined); return () => { on = false; }; }, []);
  const rollCache = useMemo(() => new Map<string, Rolled>(), [rolls, tablesForRolls]);
  const rollOf = (i: StashItem): Rolled | undefined => {
    const raw = rolls?.raw[i.instanceId];
    if (!raw || !tablesForRolls) return undefined;
    if (!rollCache.has(i.instanceId)) rollCache.set(i.instanceId, buildRolled(i, raw, tablesForRolls));
    return rollCache.get(i.instanceId);
  };
  const liteItems = useTable<ItemDef>('DestinyInventoryItemLiteDefinition');
  const breakers = useTable<any>('DestinyBreakerTypeDefinition');
  const iconDefs = useTable<IconDef>('DestinyIconDefinition');
  // as DIM's squares show them: the frame or archetype (its intrinsic perk) in the top corner, the champion it stuns by its Power
  const frameOf = (i: StashItem) => {
    if (!(isWeapon(i) || isArmor(i)) || i.tier === 6 || !liteItems) return undefined;   // DIM leaves exotics without one
    const idx = tablesForRolls?.slim[i.hash]?.cats?.find(([c]) => c === 3956125808)?.[1][0];
    const plug = idx !== undefined ? rolls?.raw[i.instanceId]?.sockets[idx]?.plugHash : undefined;
    return plug ? liteItems[plug]?.icon : undefined;
  };
  // DIM's order: the item's own breaker, else one a plugged perk or mod grants, and its list of hidden ones last
  const breakerHashOf = (i: StashItem): number | undefined => {
    const slim = tablesForRolls?.slim;
    let b: number | undefined = EXTENDED_BREAKER[i.hash] ?? slim?.[i.hash]?.breaker;
    if (!b && isWeapon(i)) b = rolls?.raw[i.instanceId]?.sockets.map((s) => (s.plugHash ? slim?.[s.plugHash]?.breaker : undefined)).find(Boolean);
    return b || undefined;
  };
  const breakerOf = (i: StashItem): string | undefined => { const b = breakerHashOf(i); return b ? breakers?.[b]?.displayProperties?.icon : undefined; };
  const godCache = useMemo(() => new Map<string, GodMark | undefined>(), [plugged, rollTable, liteItems]);
  const godOf = (i: StashItem) => {
    if (!plugged || !rollTable || !isWeapon(i)) return undefined;
    if (!liteItems) return undefined;
    const name = (hash: number) => liteItems[hash]?.name;
    if (!godCache.has(i.instanceId)) godCache.set(i.instanceId, godMarkByName(rollTable[i.hash], new Set((plugged[i.instanceId] ?? []).map(name).filter((n): n is string => !!n)), name));
    return godCache.get(i.instanceId);
  };
  // while one of our items is dragged, a drop anywhere else must not reach the browser: some (the app's built-in
  // browser among them) take dropped text for an address and open it, which reloaded the whole page
  useEffect(() => {
    if (!drag) return;
    // (only the browser's own handling is stopped; what a spot accepts is left to the spots: setting "no drop" here
    // too was the likely reason drops stopped working in the app's browser, 2026-10-19)
    const stop = (e: globalThis.DragEvent) => { if (!e.defaultPrevented) e.preventDefault(); };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => { window.removeEventListener('dragover', stop); window.removeEventListener('drop', stop); };
  }, [drag]);

  const commit = (next: Spot[]) => { live.current = next; setSpots(next); };
  useEffect(() => { if (fresh && !moved.current) commit(spotsOf(fresh)); }, [fresh]);
  // the item card closes on a click elsewhere or Escape; it stays put while the page scrolls under it
  useEffect(() => {
    if (!menu) return;
    const close = (e?: Event) => { if (!(e?.target instanceof Element && e.target.closest('.im-inspect, .im-tip'))) setMenu(undefined); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); else cardKeys.current(e); };
    window.addEventListener('click', close);
    window.addEventListener('keydown', key);
    return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', key); };
  }, [menu]);

  const chars = h.hub?.chars ?? [];
  // suggestions for the word being typed: search words first, then item names
  const [sugOpen, setSugOpen] = useState(false);
  const [sugAt, setSugAt] = useState(0);
  const lastWord = search.match(/(-?"[^"]*|\S+)$/)?.[0] ?? '';
  const suggestions: [string, string][] = (() => {
    const w = lastWord.replace(/^-/, '').replace(/"/g, '').toLowerCase();
    if (w.length < 2) return [];
    const words = SEARCH_WORDS.filter(([x]) => x.replace(/^-/, '').startsWith(w) && x !== lastWord);
    if (/^(is|power|notes):/.test(w)) return words.slice(0, 8);
    const names = [...new Set((spots ?? []).map((s) => s.item.name))].filter((n) => n.toLowerCase().includes(w)).sort((a, b) => Number(!a.toLowerCase().startsWith(w)) - Number(!b.toLowerCase().startsWith(w)) || a.localeCompare(b))
      .slice(0, 6).map((n): [string, string] => [n.includes(' ') ? `"${n}"` : n, 'An item']);
    const types = [...new Set((spots ?? []).map((s) => s.item.typeName).filter(Boolean))].filter((n) => n.toLowerCase().startsWith(w)).slice(0, 3)
      .map((n): [string, string] => [n.includes(' ') ? `"${n}"` : n, 'A type']);
    return [...words.slice(0, 4), ...types, ...names].filter(([x]) => x.toLowerCase() !== lastWord.toLowerCase()).slice(0, 10);
  })();
  const takeSuggestion = (w: string) => { setSearch(search.slice(0, search.length - lastWord.length) + (lastWord.startsWith('-') && !w.startsWith('-') ? '-' : '') + w + ' '); setSugAt(0); searchBox.current?.focus(); };
  // DIM-style keys while a card is open: L lock, V vault, 1-3 equip on that character, J junk, K keep
  cardKeys.current = (e) => {
    if (!menu || !spots || e.ctrlKey || e.metaKey || e.altKey || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    const s = live.current.find((x) => keyOf(x.item) === menu.id);
    if (!s) return;
    const k = e.key.toLowerCase();
    const c = /^[1-9]$/.test(k) ? (h.hub?.chars ?? [])[Number(k) - 1] : undefined;
    if (k === 'l') toggleLock(menu.id);
    else if (k === 'v' && s.owner !== null) { request(menu.id, null, false); setMenu(undefined); }
    else if (c && canWear(s.item, c)) { request(menu.id, c.id, true); setMenu(undefined); }
    else if (k === 'j' || k === 'k') { const t: Tag = k === 'j' ? 'junk' : 'keep'; setTag(menu.id, tags[menu.id] === t ? undefined : t); }
    else return;
    e.preventDefault();
  };
  if (!spots || !stash) return <Message title="Items" text={error ?? 'Asking Bungie what you wear, carry and keep in the vault...'}>{error && <SmallButton onClick={again}>Try again</SmallButton>}</Message>;

  const vaultAll = stash.account.filter((i) => i.bucket === VAULT).length;
  const rules = (): Rules => ({
    chars, player: h.player!,
    slotRoom: (slot) => Math.max(1, capacity(stash, slot, 10) - 1),
    vaultRoom: capacity(stash, VAULT, 600),
    vaultOthers: vaultAll - spotsOf(stash).filter((s) => s.owner === null).length,
  });

  const request = (id: string, to: Where, wear: boolean) => act(id, () => plan(live.current, id, to, wear, rules()));
  const toggleLock = (id: string) => { const s = live.current.find((x) => keyOf(x.item) === id); if (s) lockTo(id, !isLocked(s.item)); };
  const lockTo = (id: string, lock: boolean) => act(id, () => {
    const s = live.current.find((x) => keyOf(x.item) === id);
    if (!s) return 'That item is no longer there. Click Read again.';
    if (isLocked(s.item) === lock) return [];
    return [{
      label: `${lock ? 'Lock' : 'Unlock'} ${s.item.name}`,
      run: (t) => setLock(h.player!, t, s.owner ?? chars[0].id, s.item, lock),
      apply: (list) => list.map((x) => keyOf(x.item) === id ? { ...x, item: { ...x.item, state: lock ? x.item.state | 1 : x.item.state & ~1 } } : x),
    }];
  });
  // the site's own loadouts: what a character wears, kept by instance; equipping one fetches each item where it is
  const loadoutActions: LoadoutActions = {
    saved: savedLoadouts.list,
    remove: savedLoadouts.remove,
    max: (c) => {
      // kept per board state: the heads ask on every redraw, drags included
      let byChar = maxCache.get(live.current);
      if (!byChar) { byChar = new Map(); maxCache.set(live.current, byChar); }
      const m = byChar.get(c.id) ?? maxPowerSet(live.current, c);
      byChar.set(c.id, m);
      return { power: m.power, apply: () => loadoutActions.apply({ id: 'max', name: 'Max Power', classType: c.classType, at: 0, items: m.items.map(({ item: i }) => ({ instanceId: i.instanceId, hash: i.hash, name: i.name, icon: i.icon, slot: i.slot })) }, c.id) };
    },
    save: (charId, name) => {
      const c = chars.find((x) => x.id === charId);
      const items = live.current.filter((s) => s.owner === charId && s.worn)
        .map(({ item: i }) => ({ instanceId: i.instanceId, hash: i.hash, name: i.name, icon: i.icon, slot: i.slot }));
      savedLoadouts.add({ id: `${Date.now()}`, name, classType: c?.classType ?? 3, items, at: Date.now() });
    },
    apply: (l, charId) => {
      const here = l.items.map((x) => live.current.find((s) => s.item.instanceId === x.instanceId)).filter((s): s is Spot => !!s);
      const gone = l.items.filter((x) => !here.some((s) => s.item.instanceId === x.instanceId));
      // exotics last, so the one-exotic rule meets the loadout's own exotic
      for (const s of [...here].sort((a, b) => Number(exotic(a.item)) - Number(exotic(b.item)))) request(keyOf(s.item), charId, true);
      queue.current = queue.current.then(() => setNote(gone.length
        ? { text: `${l.name}: equipped what is left. Gone from your account: ${gone.map((x) => x.name).join(', ')}.`, bad: true }
        : { text: `${l.name}: equipped.` }));
    },
    // a loadout from the game: Bungie's own equip only takes what the character already carries (and shows nothing
    // here for half a minute), so the gear is fetched and worn here as for the site's loadouts, the subclass with it,
    // and then Bungie's equip puts on the rest (mods, aspects, cosmetics)
    game: (l, charId) => {
      const here = l.ids.map((id) => live.current.find((s) => s.item.instanceId === id)).filter((s): s is Spot => !!s);
      for (const s of [...here].sort((a, b) => Number(exotic(a.item)) - Number(exotic(b.item)))) request(keyOf(s.item), charId, true);
      const sub = [...(stash.equipped[charId] ?? []), ...(stash.characters[charId] ?? [])].find((i) => i.bucket === SUBCLASS_BUCKET && l.ids.includes(i.instanceId));
      const subNow = subWorn[charId] ?? (stash.equipped[charId] ?? []).find((i) => i.bucket === SUBCLASS_BUCKET)?.instanceId;
      if (sub && sub.instanceId !== subNow) wearSubclass(charId, sub);
      waiting.current++;
      queue.current = queue.current.then(async () => {
        setNote({ text: `${l.name}: putting on its mods and cosmetics...` });
        try {
          const t = await h.getToken();
          await equipLoadout(h.player!, t!, charId, l.index);
          setNote({ text: `${l.name}: equipped.` });
        } catch (e) {
          setNote({ text: `${l.name}: the gear is on, but Bungie did not put on the mods: ${e instanceof Error ? e.message : e}`, bad: true });
        }
        moved.current = true; lastMove.current = Date.now();
      }).finally(() => {
        if (--waiting.current > 0) return;
        window.clearTimeout(later.current);
        later.current = window.setTimeout(() => { if (waiting.current === 0) { moved.current = false; h.touch(); } }, Math.max(0, lastMove.current + LAG - Date.now()));
      });
    },
  };
  /** Double-click: wear it on the character holding it (from the vault: the character chosen in the header). */
  const quickEquip = (s: Spot) => {
    const c = s.owner ? chars.find((x) => x.id === s.owner) : [h.char, ...chars].find((x) => x && canWear(s.item, x));
    if (c && !s.worn) request(keyOf(s.item), c.id, true);
  };

  // one action after another: the steps are worked out when its turn comes, from where things are by then
  function act(id: string, steps0: () => Step[] | string) {
    waiting.current++;
    queue.current = queue.current.then(async () => {
      const steps = steps0();
      const name = live.current.find((s) => keyOf(s.item) === id)?.item.name ?? 'The item';
      if (typeof steps === 'string') { setNote({ text: steps, bad: true }); return; }
      if (!steps.length) return;
      setBusy((b) => new Set(b).add(id));
      try {
        const t = await h.getToken();
        if (!t) throw new Error('Your Bungie sign-in has run out. Sign in again.');
        for (const s of steps) {
          setNote({ text: `${s.label}...` });
          try { await s.run(t); } catch (e) {
            // Bungie asks for moves to be spaced out: wait a moment and try once more
            if (!(e instanceof BungieError && /Throttle/i.test(e.status))) throw e;
            await new Promise((r) => setTimeout(r, 1200));
            await s.run(t);
          }
          moved.current = true; lastMove.current = Date.now();
          commit(s.apply(live.current));
        }
        setNote({ text: `${name}: done.` });
      } catch (e) {
        setNote({ text: `${name} stopped part way: ${e instanceof Error ? e.message : e}. Click Read again to see where everything is now.`, bad: true });
      } finally {
        setBusy((b) => { const n = new Set(b); n.delete(id); return n; });
      }
    }).finally(() => {
      if (--waiting.current > 0 || !moved.current) return;
      // once Bungie has caught up, read everything again (this page and the others)
      window.clearTimeout(later.current);
      later.current = window.setTimeout(() => { if (waiting.current === 0) { moved.current = false; h.touch(); } }, Math.max(0, lastMove.current + LAG - Date.now()));
    });
  }

  // Bungie's profile can answer from a copy up to about half a minute old after a move (seen on the owner's account,
  // 2026-10-19): a read sooner shows items back where they were, so reads wait until then
  const readAgain = () => {
    window.clearTimeout(later.current);
    const left = lastMove.current + LAG - Date.now();
    if (left > 0) {
      setNote({ text: `Bungie takes up to half a minute to catch up after a move: reading again in ${Math.ceil(left / 1000)} s...` });
      later.current = window.setTimeout(readAgain, left);
      return;
    }
    moved.current = false; setNote(undefined); againRef.current();
  };
  const copies = new Map<number, number>();
  for (const s of spots ?? []) copies.set(s.item.hash, (copies.get(s.item.hash) ?? 0) + 1);
  const marks: Marks = { tagOf: (id) => tags[id], noteOf: (id) => notes[id], godOf, isNew, copiesOf: (hash) => copies.get(hash) ?? 0 };
  const searched = searchOf(search, spots, marks);
  // the one-character view (Braytech's layout): a phone keeps the board's own one-character form
  const focusView = view === 'focus' && !narrow;
  const fc = chars.find((c) => c.id === h.char?.id) ?? chars[0];
  // its quick filters: any of the chips picked in a group, and every group (each flag is a group of its own)
  const chipGroups = new Map<string, string[]>();
  if (focusView) for (const k of chips) { const g = k.includes(':') ? k.split(':')[0] : k; chipGroups.set(g, [...(chipGroups.get(g) ?? []), k]); }
  const chipOk = (s: Spot) => [...chipGroups.values()].every((ks) => ks.some((k) => {
    const i = s.item, [g, v] = k.split(':');
    if (g === 'e') return isWeapon(i) && i.damage === Number(v);
    if (g === 'a') return isWeapon(i) && i.ammo === Number(v);
    if (g === 'b') return breakerHashOf(i) === Number(v);
    if (g === 't') return (i.gearTier ?? 0) === Number(v);
    if (k === 'shaped') return isCrafted(i);
    if (k === 'mw') return isMasterwork(i);
    if (k === 'locked') return isLocked(i);
    if (k === 'god') return !!godOf(i);
    if (k === 'tagged') return !!tags[keyOf(i)];
    if (k === 'new') return isNew(keyOf(i));
    return true;
  }));
  const matches = (s: Spot) => searched(s) && (!chipGroups.size || chipOk(s));
  const hideOut = chipGroups.size > 0;   // with a filter chip on, what it rules out is left out (worn items stay, faded)
  const dragged = drag ? spots.find((s) => keyOf(s.item) === drag) : undefined;
  // may the dragged item go to this part of a slot?
  const fitsSlot = (slot: number, to: Where, wear: boolean) => !!dragged && dragged.item.slot === slot && !(dragged.owner === to && (wear ? dragged.worn : !dragged.worn))
    && (!wear || canWear(dragged.item, chars.find((c) => c.id === to)));

  // a drop zone: a character's head, worn square or the rest of their slot, or the vault's part of a slot
  const zone = (zid: string, fits: boolean, to: Where, wear: boolean, cls: string, children: ReactNode) => (
    <div key={zid} className={`${cls} ${fits ? 'can' : ''} ${over === zid && fits ? 'over' : ''}`}
      onDragOver={(e: DragEvent) => { if (!fits) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (over !== zid) setOver(zid); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(undefined); }}
      onDrop={(e) => { e.preventDefault(); setOver(undefined); setDrag(undefined); if (fits && dragged) request(keyOf(dragged.item), to, wear); }}>
      {children}
    </div>
  );

  const tile = (s: Spot, big = false) => {
    const i = s.item, id = keyOf(i);
    const god = godOf(i);
    const godWords = god && `God roll: ${[god.pve && 'PvE', god.pvp && 'PvP', god.any && !god.pve && !god.pvp && 'community pick'].filter(Boolean).join(' and ')}`;
    const facts = [i.typeName, i.power !== undefined && `Power ${i.power}`, isLocked(i) && 'Locked', isCrafted(i) && 'Shaped', isMasterwork(i) && 'Masterwork', godWords, notes[id] && `Note: ${notes[id]}`].filter(Boolean).join('  |  ');
    return (
      <div key={id} data-item={id} className={`im-tile t${i.tier} ${big ? 'big' : ''} ${isMasterwork(i) ? 'mw' : ''} ${busy.has(id) ? 'busy' : ''} ${matches(s) ? '' : 'faded'} ${drag === id ? 'lifted' : ''}`}
        draggable={!busy.has(id)} title={`${i.name}\n${facts}\n\nDrag to move or equip; click for choices; double-click to equip`}
        onDragStart={(e) => { e.dataTransfer.setData('application/x-d2c-item', id); e.dataTransfer.effectAllowed = 'move'; setDrag(id); setMenu(undefined); }}
        onDragEnd={() => { setDrag(undefined); setOver(undefined); }}
        onClick={(e) => { e.stopPropagation(); markSeen(id); setMenu({ id, x: e.clientX, y: e.clientY }); }}
        onDoubleClick={(e) => { e.stopPropagation(); setMenu(undefined); quickEquip(s); }}>
        {/* DIM's layers: the item cut out over the rarity's colour, Bungie's glows, the small season badge; items
            without a layered picture keep their flat icon and full-size watermark */}
        {(() => {
          const pic = i.iconHash ? iconDefs?.[i.iconHash] : undefined;
          return (
            <>
              <Pic src={pic?.fg ?? i.icon} className={pic ? 'im-fg' : undefined} lazy />
              {isCrafted(i) && <span className="im-layer crafted" />}
              {isMasterwork(i) && <span className={`im-layer ${i.tier === 6 ? 'mwx' : 'mw'}`} />}
              {pic ? <><span className="im-layer shade" />{pic.badge && <Pic src={pic.badge} className="im-badge" lazy />}</> : i.watermark && <Pic src={i.watermark} className="im-wm" lazy />}
            </>
          );
        })()}
        {(() => { const f = frameOf(i); return f && <Pic src={f} className="im-frameicon" lazy />; })()}
        {notes[id] && <span className="im-noted" />}
        {isNew(id) && <span className="im-new" title="New since you last looked" />}
        {tags[id] && <span className={`im-tag t-${tags[id]}`} title={TAGS.find(([t]) => t === tags[id])?.[1]}>{TAGS.find(([t]) => t === tags[id])?.[2]}</span>}
        {(i.gearTier ?? 0) > 0 && <span className={`im-tier n${i.gearTier}`} aria-label={`Tier ${i.gearTier}`}>{Array.from({ length: i.gearTier! }, (_, k) => <i key={k} />)}</span>}
        {isLocked(i) && <span className="im-lock" aria-label="Locked"><svg viewBox="0 0 10 12" width="8" height="10"><path d="M2.5 5V3.5a2.5 2.5 0 0 1 5 0V5M1.5 5h7v6h-7z" fill="currentColor" stroke="currentColor" strokeWidth="1" /></svg></span>}
        {i.power !== undefined && (
          <span className="im-foot">
            {god && <GodThumb mark={god} />}
            {(() => { const b = breakerOf(i); return b && <Pic src={b} className="im-breaker" />; })()}
            {isWeapon(i) && elementIcons?.[i.damage] ? <Pic src={elementIcons[i.damage]} className="im-elicon" /> : <i className={`im-el e${isWeapon(i) ? i.damage : 0}`} />}
            <b>{i.power}</b>
          </span>
        )}
      </div>
    );
  };

  // junk-tagged items go to the end of their slot, whatever else the order
  const junk = (s: Spot) => Number(tags[keyOf(s.item)] === 'junk')
  // an item's perks by name (for the table): what is plugged and what its columns can switch to
  const perkNames = (i: StashItem) => {
    if (!plugged || !liteItems) return [];
    const out = new Set<string>();
    for (const p of plugged[i.instanceId] ?? []) {
      const d = liteItems[p];
      if (d?.name && !/^(Empty|Default)/.test(d.name) && !/Tracker$/.test(d.name) && !/Shader|Ornament|Tracker|Memento|Masterwork|Catalyst|Emote|Transmat|Projection/.test(d.type)) out.add(d.name);
    }
    return [...out].slice(0, 12);
  };
  const sorter = (SORTS.find(([k]) => k === sort) ?? SORTS[0])[2];
  const at = (owner: Where, slot: number, worn: boolean) => spots.filter((s) => s.owner === owner && s.worn === worn && s.item.slot === slot && (worn || !hideOut || matches(s))
    && !(owner === null && isArmor(s.item) && (focusView ? fc && s.item.classType !== 3 && s.item.classType !== fc.classType : hiddenClasses.has(s.item.classType)))).sort((a, b) => junk(a) - junk(b) || sorter(a, b));
  // the vault's part of a slot: one list, or groups (View menu) each with a header that folds it in every slot
  const isJunk = (s: Spot) => tags[keyOf(s.item)] === 'junk';
  const grouper = GROUPS.find(([k]) => k === layout.group)?.[2] ?? null;
  // grouped for real only where there are groups to show (junk apart: only slots that hold some junk)
  const hasGroups = (slot: number) => !!grouper || (layout.junkApart && at(null, slot, false).some(isJunk));
  const fold = (k: string) => setLayout((l) => ({ ...l, folded: l.folded.includes(k) ? l.folded.filter((x) => x !== k) : [...l.folded, k] }));
  const vaultCell = (slot: number) => {
    const list = at(null, slot, false);
    const junkList = layout.junkApart ? list.filter(isJunk) : [];
    const rest = layout.junkApart ? list.filter((s) => !isJunk(s)) : list;
    if (!grouper && !junkList.length) return rest.map((s) => tile(s));
    const groups = grouper ? groupSpots(rest, grouper, marks).map((g) => ({ ...g, key: `${layout.group}:${g.key}` })) : [{ key: '', name: '', items: rest }];
    if (junkList.length) groups.push({ key: 'junk', name: 'Junk', items: junkList });
    return groups.filter((g) => g.items.length).map((g) => {
      const shut = !!g.key && layout.folded.includes(g.key);
      return (
        <div key={g.key || 'all'} className={`im-grp ${shut ? 'shut' : ''} ${g.key === 'junk' ? 'junk' : ''}`}>
          {g.key && (
            <button type="button" className="im-grp-head" title={shut ? `Show ${g.name} (in every slot)` : `Fold ${g.name} away (in every slot)`} onClick={(e) => { e.stopPropagation(); fold(g.key); }}>
              <span className="im-caret">▾</span>{g.name}<b>{g.items.length}</b>
            </button>
          )}
          {!shut && <div className="im-grp-items">{g.items.map((s) => tile(s))}</div>}
        </div>
      );
    });
  };
  const vaultCount = vaultAll - spotsOf(stash).filter((s) => s.owner === null).length + spots.filter((s) => s.owner === null).length;
  const vaultCap = capacity(stash, VAULT, 0);
  // characters hidden from the board (DIM-style), and with them their class's armour in the vault
  const shown = chars.filter((c) => !hidden.includes(c.id));
  // a phone shows one character at a time (the one picked, else the one chosen in the header), with the vault under it
  const board = narrow ? [chars.find((c) => c.id === focus) ?? chars.find((c) => c.id === h.char?.id) ?? chars[0]].filter(Boolean) : shown;
  const hiddenClasses = new Set(chars.filter((c) => hidden.includes(c.id)).map((c) => c.classType));
  // after the Postmaster is emptied: read everything again once Bungie has caught up
  const collected = () => {
    moved.current = true; lastMove.current = Date.now();
    window.clearTimeout(later.current);
    later.current = window.setTimeout(() => { if (waiting.current === 0) { moved.current = false; h.touch(); } }, LAG);
  };
  // a subclass is not on the board: it is worn straight away, in the same queue as the moves
  const wearSubclass = (cid: string, i: StashItem) => act(i.instanceId, () => [{
    label: `Wearing ${i.name}`,
    run: (t) => equipItem(h.player!, t, cid, i),
    apply: (list) => { setSubWorn((m) => ({ ...m, [cid]: i.instanceId })); return list; },
  }]);

  return (
    <div className={`im-root ${layout.dense ? 'dense' : 'roomy'}`} style={{ ['--tile' as string]: `${size}px`, ['--k' as string]: size / 50 }} onClick={() => setMenu(undefined)}>
      <div className="im-bar">
        <span className="im-searchwrap">
          <input ref={searchBox} className="g-input im-search" placeholder="Search: a name, or is:godroll, is:dupelower, power:>540...  (press / )" title={SEARCH_HELP} value={search}
            onChange={(e) => { setSearch(e.target.value); setSugAt(0); setSugOpen(true); }} onFocus={() => setSugOpen(true)} onBlur={() => window.setTimeout(() => setSugOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { if (sugOpen && suggestions.length) setSugOpen(false); else { setSearch(''); e.currentTarget.blur(); } return; }
              if (!sugOpen || !suggestions.length) return;
              if (e.key === 'ArrowDown') { e.preventDefault(); setSugAt((sugAt + 1) % suggestions.length); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setSugAt((sugAt - 1 + suggestions.length) % suggestions.length); }
              if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); takeSuggestion(suggestions[sugAt][0]); }
            }} aria-label="Search items" aria-autocomplete="list" />
          {sugOpen && suggestions.length > 0 && (
            <div className="im-sug" role="listbox">
              {suggestions.map(([w, what], k) => (
                <button key={w} type="button" role="option" aria-selected={k === sugAt} className={k === sugAt ? 'on' : ''} onMouseDown={(e) => { e.preventDefault(); takeSuggestion(w); }}>
                  <code>{w}</code><span>{what}</span>
                </button>
              ))}
            </div>
          )}
        </span>
        <span className="im-help">
          <button type="button" className={`im-help-btn ${help ? 'on' : ''}`} title="Search words" onClick={(e) => { e.stopPropagation(); setHelp(!help); }}>?</button>
          {help && (
            <div className="im-help-pop" onClick={(e) => e.stopPropagation()}>
              <div className="im-help-head">Tap a word to add it. Put - in front of one to turn it round.</div>
              {SEARCH_WORDS.map(([w, what]) => <button key={w} type="button" title={what} onClick={() => setSearch((cur) => (cur.trim() ? `${cur.trim()} ${w}` : w))}><code>{w}</code><span>{what}</span></button>)}
            </div>
          )}
        </span>
        <button type="button" className="im-iconbtn" onClick={readAgain} title="Read again: ask Bungie where everything is now" aria-label="Read again">
          <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v3h-3" /></svg>
        </button>
        {(() => { const n = spots.filter((s) => isNew(keyOf(s.item))).length; return n > 0 && <SmallButton onClick={clearNew} title="Clears the new-item dots (search is:new to see them)">Clear {n} new</SmallButton>; })()}
        {narrow && <span className="im-sizes im-chars pick" role="group" aria-label="Character shown">{chars.map((c) => <button key={c.id} type="button" className={board[0]?.id === c.id ? 'on' : ''} onClick={() => setFocus(c.id)}>{c.cls}</button>)}</span>}
        <span className="im-viewwrap">
          <button type="button" className={`im-viewbtn ${viewOpen ? 'on' : ''}`} onClick={(e) => { e.stopPropagation(); setViewOpen(!viewOpen); }} aria-expanded={viewOpen}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 4h12M2 8h12M2 12h12M5 2.5v3M11 6.5v3M7 10.5v3" /></svg>
            View
          </button>
          {viewOpen && (
            <div className="im-viewpop" onClick={(e) => e.stopPropagation()}>
              <label><span>Layout</span>
                <span className="im-seg">{([['focus', 'Character', 'One character at a time with the vault, quick filters and Power beside it'], ['board', 'Board', 'Every character and the vault side by side'], ['table', 'Table', 'Every item as a table you can sort, like the Organizer in DIM']] as const).map(([v, label, tip]) => <button key={v} type="button" className={view === v ? 'on' : ''} title={tip} onClick={() => setView(v)}>{label}</button>)}</span>
              </label>
              <label><span>Group the vault by</span>
                <select value={layout.group} onChange={(e) => { const group = e.target.value; setLayout({ ...layout, group, folded: group === 'copies' && !layout.folded.includes('copies:c-') ? [...layout.folded, 'copies:c-'] : layout.folded }); }}>{GROUPS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}</select>
              </label>
              <label className="check"><input type="checkbox" checked={layout.junkApart} onChange={(e) => setLayout({ ...layout, junkApart: e.target.checked })} /><span>Junk in its own folded group at the end</span></label>
              <label><span>Sort</span>
                <select value={sort} onChange={(e) => setSort(e.target.value)}>{SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}</select>
              </label>
              <label><span>Squares</span>
                <span className="im-seg">{SIZES.map(([s, label]) => <button key={s} type="button" className={s === size ? 'on' : ''} onClick={() => setSize(s)}>{label}</button>)}</span>
              </label>
              <label><span>Spacing</span>
                <span className="im-seg">{([[true, 'Compact'], [false, 'Roomy']] as const).map(([d, label]) => <button key={label} type="button" className={layout.dense === d ? 'on' : ''} onClick={() => setLayout({ ...layout, dense: d })}>{label}</button>)}</span>
              </label>
              {!narrow && view === 'board' && (
                <label><span>Characters</span>
                  <span className="im-seg">{chars.map((c) => <button key={c.id} type="button" className={hidden.includes(c.id) ? '' : 'on'} title={hidden.includes(c.id) ? `Show your ${c.cls}` : `Hide your ${c.cls} and their armour in the vault`}
                    onClick={() => setHidden(hidden.includes(c.id) ? hidden.filter((x) => x !== c.id) : shown.length > 1 ? [...hidden, c.id] : hidden)}>{c.cls}</button>)}</span>
                </label>
              )}
              {layout.folded.length > 0 && <button type="button" className="im-viewlink" onClick={() => setLayout({ ...layout, folded: [] })}>Unfold every group</button>}
            </div>
          )}
        </span>
        {note && <span className={`im-note ${note.bad ? 'bad' : ''}`}>{note.text}</span>}
      </div>

      {(search.trim() || chipGroups.size > 0) && (() => {
        // DIM's bulk actions, on what the search shows (in the one-character view: that character's and the vault's)
        const onFocus = (s: Spot) => s.owner === fc?.id || (s.owner === null && !(isArmor(s.item) && s.item.classType !== 3 && s.item.classType !== fc?.classType));
        const hits = spots.filter((s) => matches(s) && (!focusView || onFocus(s)));
        const odd = unknownWords(search);
        if (odd.length) return <div className="im-bulk"><span className="im-odd">The search does not know {odd.join(', ')}. Tap ? beside the search for its words.</span></div>;
        if (!hits.length) return <div className="im-bulk"><span>Nothing matches.</span></div>;
        const carried = hits.filter((s) => s.owner !== null && !s.worn);
        const ask = (n: number, what: string) => n <= 5 || confirm(`${what} ${n} items?`);
        return (
          <div className="im-bulk">
            <span><b>{hits.length}</b> shown:</span>
            <select value="" aria-label="Tag every item shown" onChange={(e) => {
              const v = e.target.value as Tag | 'clear';
              if (v && ask(hits.length, v === 'clear' ? 'Clear the tags of' : `Tag ${v}`)) for (const s of hits) setTag(keyOf(s.item), v === 'clear' ? undefined : v);
            }}>
              <option value="">Tag all...</option>
              {TAGS.map(([t, label]) => <option key={t} value={t}>{label}</option>)}
              <option value="clear">Clear tags</option>
            </select>
            <button type="button" onClick={() => { const todo = hits.filter((s) => !isLocked(s.item)); if (todo.length && ask(todo.length, 'Lock')) todo.forEach((s) => lockTo(keyOf(s.item), true)); }}>Lock all</button>
            <button type="button" onClick={() => { const todo = hits.filter((s) => isLocked(s.item)); if (todo.length && ask(todo.length, 'Unlock')) todo.forEach((s) => lockTo(keyOf(s.item), false)); }}>Unlock all</button>
            <button type="button" disabled={!carried.length} title="Worn items stay where they are" onClick={() => { if (ask(carried.length, 'Move to the vault')) carried.forEach((s) => request(keyOf(s.item), null, false)); }}>
              Put {carried.length || ''} in the vault
            </button>
          </div>
        );
      })()}
      {view === 'table' && <Organizer spots={spots} matches={matches} chars={chars} godOf={godOf} tags={tags} perkNames={perkNames} elementIcons={elementIcons} open={(id, x, y) => setMenu({ id, x, y })} />}
      {focusView && fc && (
        <div className="im-focus">
          {/* the characters down the left: pick one, or drop an item on one to send it there */}
          <nav className="im-rail" aria-label="Characters">
            {chars.map((c) => zone(`r|${c.id}`, !!dragged && dragged.owner !== c.id, c.id, false, 'im-railzone',
              <button type="button" className={`im-railchar ${c.id === fc.id ? 'on' : ''}`} title={`${c.cls} (${c.race}), Power ${c.light}${dragged ? `: drop to send ${dragged.item.name} here` : ''}`} onClick={() => h.setChar(c.id)}>
                <Pic src={c.emblem} width={44} height={44} />
                <small>{c.cls}</small>
              </button>))}
            {zone('r|vault', !!dragged && dragged.owner !== null, null, false, 'im-railzone',
              <span className="im-railchar vault" title={dragged ? `Drop to put ${dragged.item.name} in the vault` : `Vault: ${vaultCap ? `${vaultCount} / ${vaultCap}` : vaultCount} spaces used`}>
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3.5 4h17v16h-17zM8 12a4 4 0 1 0 8 0a4 4 0 1 0-8 0" /></svg>
                <small>Vault</small>
              </span>)}
          </nav>

          <div className="im-fleft">
            <FilterPanel chips={chips} setChips={setChips} elementIcons={elementIcons} breakers={breakers} />
            {h.own && <GameLoadouts c={fc} equip={(l) => loadoutActions.game(l, fc.id)} />}
          </div>

          <div className="im-fmain">
            {/* the character's subclasses (the one worn lit), and the way to see them in 3D */}
            <div className="im-fslot">
              <div className="im-fslot-head">Subclass</div>
              <div className="im-subs">
                {(() => {
                  const worn = (stash.equipped[fc.id] ?? []).filter((i) => i.bucket === SUBCLASS_BUCKET);
                  const held = (stash.characters[fc.id] ?? []).filter((i) => i.bucket === SUBCLASS_BUCKET);
                  const on = subWorn[fc.id] ?? worn[0]?.instanceId;
                  return [...worn, ...held].sort((a, b) => a.damage - b.damage).map((i) => (
                    <button key={i.instanceId} type="button" className={`im-sub ${i.instanceId === on ? 'on' : ''} ${busy.has(i.instanceId) ? 'busy' : ''}`} title={i.instanceId === on ? `${i.name} (worn)` : `${i.name}: click to wear it`}
                      disabled={i.instanceId === on || !h.own} onClick={(e) => { e.stopPropagation(); wearSubclass(fc.id, i); }}>
                      <Pic src={i.icon} />
                    </button>
                  ));
                })()}
                <button type="button" className="im-3d" title={`See your ${fc.cls} in 3D, in what they wear now`} onClick={() => nav('/guardian/character/3d-view')}>
                  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"><path d="M8 1.5l5.5 3v7L8 14.5l-5.5-3v-7zM2.5 4.5L8 7.5l5.5-3M8 7.5v7" /></svg>
                  View in 3D
                </button>
              </div>
            </div>
            <Postmaster stash={stash} chars={[fc]} onCollected={collected} />
            {SECTIONS.map((sec) => {
              const shut = open[sec.title] === false;
              return (
                <section key={sec.title} className="im-section">
                  <button type="button" className="im-sechead" onClick={() => setOpen({ ...open, [sec.title]: shut })}>
                    <span className={`im-caret ${shut ? 'shut' : ''}`}>▾</span>{sec.title.toUpperCase()}
                  </button>
                  {!shut && sec.slots.map(([slot, label]) => {
                    const worn = at(fc.id, slot, true)[0];
                    const carried = at(fc.id, slot, false);
                    const room = Math.max(1, capacity(stash, slot, 10) - 1);
                    const all = fullVaults.includes(slot);
                    return (
                      <div key={slot} className="im-fslot">
                        <div className="im-fslot-head">{sec.title === 'Weapons' ? `${label} Weapons` : label}{!hideOut && <small>{carried.length} / {room}</small>}</div>
                        <div className="im-fslot-body">
                          {zone(`${fc.id}|${slot}|w`, fitsSlot(slot, fc.id, true), fc.id, true, 'im-worn', worn ? tile(worn, true) : <span className="im-empty big" />)}
                          {zone(`${fc.id}|${slot}|c`, fitsSlot(slot, fc.id, false), fc.id, false, 'im-carried im-fcarried',
                            [...carried.map((s) => tile(s)), ...Array.from({ length: hideOut ? 0 : Math.max(0, room - carried.length) },(_, k) => <span key={`e${k}`} className="im-empty" />)])}
                          <VaultFold open={all} toggle={() => setFullVaults(all ? fullVaults.filter((x) => x !== slot) : [...fullVaults, slot])} count={at(null, slot, false).length}>
                            {zone(`v|${slot}`, fitsSlot(slot, null, false), null, false, `im-vault ${hasGroups(slot) ? 'grouped' : ''}`, vaultCell(slot))}
                          </VaultFold>
                        </div>
                      </div>
                    );
                  })}
                </section>
              );
            })}
          </div>

          {/* beside it, as Braytech has it: the character, the Power of what they wear, the vault and currencies */}
          <aside className="im-fside">
            <CharHead c={fc} spots={spots} stats={stash.stats?.[fc.id] ?? []} actions={loadoutActions} />
            <div className="im-fbox">
              <div className="im-fbox-title">Power</div>
              {(() => {
                const worn = SECTIONS.slice(0, 2).flatMap((sec) => sec.slots.map(([slot]) => at(fc.id, slot, true)[0])).filter((s): s is Spot => !!s && s.item.power !== undefined);
                const avg = worn.length ? worn.reduce((n, s) => n + (s.item.power ?? 0), 0) / worn.length : 0;
                return (
                  <>
                    {worn.map((s) => (
                      <button key={keyOf(s.item)} type="button" className={`im-fpow t${s.item.tier}`} onClick={(e) => { e.stopPropagation(); setMenu({ id: keyOf(s.item), x: e.clientX, y: e.clientY }); }}>
                        <Pic src={s.item.icon} width={20} height={20} /><span>{s.item.name}</span><b>{s.item.power}</b>
                      </button>
                    ))}
                    <div className="im-fpow total"><span>Average of what is worn</span><b>{Math.floor(avg)}<small>{(avg % 1).toFixed(2).slice(1)}</small></b></div>
                  </>
                );
              })()}
            </div>
            <div className="im-fbox">
              <div className="im-fbox-title">Vault<small className={vaultCap && vaultCount >= vaultCap - 25 ? 'warn' : ''}>{vaultCap ? `${vaultCount} / ${vaultCap}` : vaultCount}</small></div>
              {vaultCap > 0 && <div className="im-fbar"><i style={{ width: `${Math.min(100, (vaultCount / vaultCap) * 100)}%` }} /></div>}
            </div>
            {stash.currencies.length > 0 && (
              <div className="im-fbox">
                <div className="im-fbox-title">Currencies</div>
                {stash.currencies.map((m) => <div key={m.name} className="im-fcur"><Pic src={m.icon} width={16} height={16} /><span>{m.name}</span><b>{m.quantity.toLocaleString()}</b></div>)}
              </div>
            )}
          </aside>
        </div>
      )}
      <div className={`im-board ${narrow ? 'narrow' : ''}`} hidden={view === 'table' || focusView} style={{ ['--chars' as string]: board.length }}>
        <div className="im-heads">
          {board.map((c) => zone(`h|${c.id}`, !!dragged && dragged.owner !== c.id, c.id, false, 'im-headzone', <CharHead key={c.id} c={c} spots={spots} stats={stash.stats?.[c.id] ?? []} actions={loadoutActions} />))}
          <div className="im-head vault">
            <span className="im-vaulticon"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3.5 4h17v16h-17zM8 12a4 4 0 1 0 8 0a4 4 0 1 0-8 0" /></svg></span>
            <span className="im-head-words"><b>Vault</b><small className={vaultCap && vaultCount >= vaultCap - 25 ? 'warn' : ''} title={vaultCap && vaultCount >= vaultCap - 25 ? 'Nearly full: moves between characters pass through the vault and need a free space' : undefined}>{vaultCap ? `${vaultCount} / ${vaultCap}` : vaultCount} spaces used</small></span>
            <span className="im-currency">{stash.currencies.slice(0, 4).map((m) => <span key={m.name} title={m.name}><Pic src={m.icon} width={14} height={14} />{m.quantity.toLocaleString()}</span>)}</span>
          </div>
        </div>

        <Postmaster stash={stash} chars={board} onCollected={collected} />

        {SECTIONS.map((sec) => {
          const shut = open[sec.title] === false;
          return (
            <section key={sec.title} className="im-section">
              <button type="button" className="im-sechead" onClick={() => setOpen({ ...open, [sec.title]: shut })}>
                <span className={`im-caret ${shut ? 'shut' : ''}`}>▾</span>{sec.title.toUpperCase()}
              </button>
              {!shut && sec.slots.map(([slot, label]) => (
                <div key={slot} className="im-row" title={label}>
                  {!narrow && <span className="im-rowlabel">{label}</span>}
                  {narrow && <div className="im-slotname">{label}</div>}
                  {board.map((c) => {
                    const worn = at(c.id, slot, true)[0];
                    const carried = at(c.id, slot, false);
                    return (
                      <div key={c.id} className="im-cell">
                        {zone(`${c.id}|${slot}|w`, fitsSlot(slot, c.id, true), c.id, true, 'im-worn', worn ? tile(worn, true) : <span className="im-empty big" />)}
                        {zone(`${c.id}|${slot}|c`, fitsSlot(slot, c.id, false), c.id, false, `im-carried ${carried.length >= Math.max(1, capacity(stash, slot, 10) - 1) ? 'full' : ''}`, carried.map((s) => tile(s)))}
                      </div>
                    );
                  })}
                  {narrow && !openVaults.includes(slot) ? (
                    <button type="button" className="im-vaultfold" onClick={() => setOpenVaults([...openVaults, slot])}>
                      <span>Vault</span><b>{at(null, slot, false).length}</b><span className="im-caret shut">▾</span>
                    </button>
                  ) : (
                    <>
                      {narrow && <button type="button" className="im-vaultfold open" onClick={() => setOpenVaults(openVaults.filter((x) => x !== slot))}><span>Vault</span><b>{at(null, slot, false).length}</b><span className="im-caret">▾</span></button>}
                      {zone(`v|${slot}`, fitsSlot(slot, null, false), null, false, `im-vault ${hasGroups(slot) ? 'grouped' : ''}`, vaultCell(slot))}
                    </>
                  )}
                </div>
              ))}
            </section>
          );
        })}
      </div>

      {menu && (() => {
        const s = spots.find((x) => keyOf(x.item) === menu.id);
        if (!s) return null;
        const i = s.item;
        const go = (to: Where, wear: boolean) => { setMenu(undefined); request(menu.id, to, wear); };
        const cls = s.owner ? chars.find((c) => c.id === s.owner)?.classType : i.classType;
        // infusion fuel: anything held for the same slot with more Power (armour: one its class can wear)
        const fuel = (i.power === undefined ? [] : spots.filter((x) => x !== s && x.item.slot === i.slot && (x.item.power ?? 0) > (i.power ?? 0)
          && (isWeapon(i) || x.item.classType === 3 || x.item.classType === i.classType)))
          .sort((x, y) => junk(y) - junk(x) || (y.item.power ?? 0) - (x.item.power ?? 0)).slice(0, 6);
        return (
          <ItemInspect key={menu.id} spot={s} chars={chars} narrow={narrow} at={{ x: menu.x, y: menu.y }} rolled={rollOf(i)}
            busy={busy.has(menu.id)} tag={tags[menu.id]} setTag={(t) => setTag(menu.id, t)}
            note={notes[menu.id] ?? ''} setNote={(v) => setNoteFor(menu.id, v.trim() || undefined)}
            god={godOf(i)} groups={rollTable?.[i.hash] ?? []} elementIcons={elementIcons}
            copies={spots.filter((x) => x !== s && x.item.hash === i.hash)} fuel={fuel} copyTag={(id) => tags[id]}
            loadouts={savedLoadouts.list.filter((l) => i.classType === 3 ? (cls === undefined || cls === 3 || l.classType === cls || l.classType === 3) : l.classType === i.classType)}
            onLock={() => toggleLock(menu.id)} go={go}
            compare={() => { setMenu(undefined); setCompare(menu.id); }}
            open={(id) => setMenu({ ...menu, id })}
            infuse={(f) => {
              // infusing happens in the game: bring the item and its fuel to one character
              const to = s.owner ?? (h.char && chars.some((c) => c.id === h.char!.id) ? h.char.id : chars[0]?.id);
              if (!to) return;
              setMenu(undefined);
              if (s.owner !== to) request(menu.id, to, false);
              if (f.owner !== to) request(keyOf(f.item), to, false);
              queue.current = queue.current.then(() => setNote({ text: `${i.name} and ${f.item.name} are on your ${chars.find((c) => c.id === to)?.cls ?? 'character'}: infuse at the inventory screen in the game.` }));
            }}
            addToLoadout={(l) => savedLoadouts.add({ ...l, at: Date.now(), items: [...l.items.filter((x) => x.slot !== i.slot), { instanceId: i.instanceId, hash: i.hash, name: i.name, icon: i.icon, slot: i.slot }] })}
            info={() => { setMenu(undefined); nav(`/guardian/operations/d2-info?item=${i.hash}`); }} />
        );
      })()}
      {compare && (() => { const b = spots.find((x) => keyOf(x.item) === compare); return b ? <Compare base={b.item} spots={spots} chars={chars} godOf={godOf} rollOf={rollOf} tags={tags} setTag={setTag} close={() => setCompare(undefined)} /> : null; })()}
      <p className="g-dim small">Drag an item onto a character (or their banner) to move it there, onto the big square to equip it, or onto the vault to put it away. Double-click to equip; click for more. Items glow while Bungie works.</p>
      <p className="g-dim small">{sync === 'synced' ? 'Your tags, notes and loadouts are kept with your Bungie account, so they follow you to other devices.' : sync === 'reading' ? 'Reading your tags, notes and loadouts...' : 'Your tags, notes and loadouts are kept in this browser.'}</p>
    </div>
  );
}

/** A character's head: the emblem's banner, class and Power, and the game's loadouts to equip. */
function CharHead({ c, spots, stats, actions }: { c: HubChar; spots: Spot[]; stats: GearStat[]; actions: LoadoutActions }) {
  const h = useHub();
  const [list, setList] = useState(false);
  const worn = spots.filter((s) => s.owner === c.id && s.worn && (isWeapon(s.item) || isArmor(s.item)) && s.item.power !== undefined);
  const power = worn.length ? Math.floor(worn.reduce((n, s) => n + (s.item.power ?? 0), 0) / worn.length) : c.light;
  const maxP = actions.max(c).power;
  return (
    <>
      <div className="im-head">
        {c.banner && <Pic src={c.banner} className="im-head-banner" />}
        <Pic src={c.emblem} width={36} height={36} className="im-head-emblem" />
        <span className="im-head-words"><b>{c.cls}</b><small>{c.race}</small></span>
        <span className="im-head-power" title="Power of the gear worn now">{power}{maxP > power && <small title="The most Power this character can reach with what you hold (Equip max Power is in the ⋮ menu)">max {maxP}</small>}</span>
        <button type="button" className="im-head-more" title="Loadouts: save what this character wears, or equip one" onClick={(e) => { e.stopPropagation(); setList(!list); }}>⋮</button>
        {list && h.own && <Loadouts c={c} close={() => setList(false)} actions={actions} />}
      </div>
      {stats.length > 0 && <div className="im-head-stats">{stats.map((s) => <span key={s.name} title={s.name}><Pic src={s.icon} width={14} height={14} />{s.value}</span>)}</div>}
    </>
  );
}

// ---------------- loadouts: the site's own (kept in this browser) and the game's ----------------

export interface SavedItem { instanceId: string; hash: number; name: string; icon: string; slot: number }
export interface SavedLoadout { id: string; name: string; classType: number; items: SavedItem[]; at: number }

function useSavedLoadouts(account?: string) {
  const key = `d2c.loadouts.${account ?? ''}`;
  const [list, setList] = useState<SavedLoadout[]>([]);
  useEffect(() => { try { setList(JSON.parse(localStorage.getItem(key) ?? '[]')); } catch { setList([]); } }, [key]);
  const save = (next: SavedLoadout[]) => { setList(next); try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage blocked */ } };
  /** The account's loadouts: added to this browser's (replace: instead of them). */
  const merge = (more: SavedLoadout[], replace = false) => setList((cur) => {
    const next = replace ? more : [...cur.filter((l) => !more.some((m) => m.id === l.id)), ...more];
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage blocked */ }
    return next;
  });
  return {
    list,
    add: (l: SavedLoadout) => save([...list.filter((x) => x.id !== l.id), l]),
    remove: (id: string) => save(list.filter((x) => x.id !== id)),
    merge,
  };
}

interface LoadoutActions { saved: SavedLoadout[]; save: (charId: string, name: string) => void; apply: (l: SavedLoadout, charId: string) => void; game: (l: Loadout, charId: string) => void; remove: (id: string) => void; max: (c: HubChar) => { power: number; apply: () => void } }

function Loadouts({ c, close, actions }: { c: HubChar; close: () => void; actions: LoadoutActions }) {
  const h = useHub();
  const { data, error } = usePrivate(`loadouts:${c.id}`, (t) => loadLoadouts(h.player!, t, c.id));
  const [note, setNote] = useState<string>();
  const [name, setName] = useState('');
  useEffect(() => { window.addEventListener('click', close); return () => window.removeEventListener('click', close); }, [close]);
  const mine = actions.saved.filter((l) => l.classType === c.classType).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="g-menu im-loadouts" role="menu" onClick={(e) => e.stopPropagation()}>
      {(() => { const m = actions.max(c); return m.power > 0 && <button type="button" role="menuitem" className="im-lo-max" title="Equips the highest Power you hold for each weapon and armour slot (one exotic of each), from wherever it is" onClick={() => { m.apply(); close(); }}>Equip max Power <b>{m.power}</b></button>; })()}
      <div className="g-menu-sep" />
      <div className="g-menu-head">Your loadouts ({c.cls})</div>
      {mine.length === 0 && <div className="im-menu-text">None saved yet. Save what your {c.cls} wears now:</div>}
      {mine.map((l) => (
        <div key={l.id} className="im-lo-row">
          <span className="im-lo-icons">{l.items.slice(0, 3).map((i) => <Pic key={i.instanceId} src={i.icon} width={20} height={20} />)}</span>
          <span className="im-lo-name" title={l.items.map((i) => i.name).join('\n')}>{l.name}<small>{l.items.length} items</small></span>
          <button type="button" title={`Equip ${l.name} on your ${c.cls} (items come from the vault or your other characters)`} onClick={() => { actions.apply(l, c.id); close(); }}>Equip</button>
          <button type="button" className="del" title={`Delete ${l.name}`} onClick={() => { if (confirm(`Delete the loadout "${l.name}"?`)) actions.remove(l.id); }}>✕</button>
        </div>
      ))}
      <form className="im-lo-save" onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; actions.save(c.id, name.trim()); setName(''); setNote(`Saved "${name.trim()}".`); }}>
        <input className="g-input" placeholder="Name, e.g. Raid DPS" value={name} onChange={(e) => setName(e.target.value)} aria-label="Loadout name" />
        <button type="submit" disabled={!name.trim()}>Save worn</button>
      </form>
      <div className="g-menu-sep" />
      <div className="g-menu-head">In the game</div>
      {!data ? <div className="im-menu-text">{error ?? 'Asking Bungie...'}</div>
        : !data.some((l) => l.items > 0) ? <div className="im-menu-text">No loadouts saved in the game.</div>
          : data.filter((l) => l.items > 0).map((l) => (
            <button key={l.index} type="button" role="menuitem" onClick={() => { actions.game(l, c.id); close(); }}>
              <span className="im-lo-badge">{l.color && <Pic src={l.color} />}{l.icon && <Pic src={l.icon} />}</span>
              <span>{l.name}</span>
            </button>
          ))}
      {note && <div className="im-menu-text">{note}</div>}
    </div>
  );
}

/** What waits at the Postmaster for each character, with the game's Collect button. */
function Postmaster({ stash, chars, onCollected }: { stash: Stash; chars: HubChar[]; onCollected: () => void }) {
  const h = useHub();
  const [busy, setBusy] = useState<string>();
  const [note, setNote] = useState<string>();
  const lost = chars.map((c) => ({ c, items: (stash.characters[c.id] ?? []).filter((i) => i.bucket === LOST_ITEMS) }));
  if (!lost.some((l) => l.items.length)) return null;
  return (
    <section className="im-section">
      <div className="im-sechead static">POSTMASTER</div>
      <div className="im-row">
        {lost.map(({ c, items }) => (
          <div key={c.id} className="im-cell post">
            <div className="im-postitems">
              {items.map((i, k) => (
                <div key={i.instanceId || `${i.hash}:${k}`} className={`im-tile t${i.tier}`} title={`${i.name}\n${i.typeName}`}>
                  <Pic src={i.icon} lazy />
                  {(i.power !== undefined || i.quantity > 1) && <span className="im-foot">{i.power ?? i.quantity}</span>}
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <button type="button" className="im-collect" disabled={busy === c.id} onClick={async () => {
                setBusy(c.id); setNote(undefined);
                let moved = 0; let refused: string | undefined;
                const t = await h.getToken();
                for (const i of items) {
                  try { await pullFromPostmaster(h.player!, t!, c.id, i); moved++; } catch (e) { refused ??= `${i.name}: ${e instanceof Error ? e.message : e}`; }
                }
                setBusy(undefined);
                setNote(refused ? `Collected ${moved} of ${items.length}. ${refused}` : `Collected ${moved} item(s). They show here once Bungie catches up (about half a minute).`);
                if (moved) onCollected();
              }}>{busy === c.id ? 'Collecting...' : `Collect Postmaster (${items.length})`}</button>
            )}
          </div>
        ))}
        <div className="im-vault post">{note && <span className="im-note">{note}</span>}</div>
      </div>
    </section>
  );
}

// ---------------- the one-character view's parts ----------------

const ELEMENT_CHIPS: [number, string][] = [[1, 'Kinetic'], [3, 'Solar'], [2, 'Arc'], [4, 'Void'], [6, 'Stasis'], [7, 'Strand']];
const AMMO_CHIPS: [number, string, string][] = [[1, 'Primary', '#e8e8e8'], [2, 'Special', '#7fe0a4'], [3, 'Heavy', '#b185df']];
const FLAG_CHIPS: [string, string, string][] = [
  ['shaped', 'Shaped', 'Crafted at the Enclave'], ['mw', 'Masterwork', 'Masterworked'], ['locked', 'Locked', 'Locked in the game'],
  ['god', 'God roll', 'A PvE or PvP god roll, or a community pick'], ['tagged', 'Tagged', 'Has one of your tags'], ['new', 'New', 'New since you last looked'],
];

/** The quick filters down the left of the one-character view: within a group any chip counts, across groups all must. */
function FilterPanel({ chips, setChips, elementIcons, breakers }: { chips: string[]; setChips: (c: string[]) => void; elementIcons?: Record<number, string>; breakers?: Record<string, any> }) {
  const flip = (k: string) => setChips(chips.includes(k) ? chips.filter((x) => x !== k) : [...chips, k]);
  const chip = (k: string, label: string, icon: ReactNode, tip = label) => (
    <button key={k} type="button" className={`im-chip ${chips.includes(k) ? 'on' : ''}`} title={tip} aria-pressed={chips.includes(k)} onClick={() => flip(k)}>
      <span className="im-chip-icon">{icon}</span><span className="im-chip-label">{label}</span>
    </button>
  );
  // Bungie's three champion breakers (shield piercing, disruption, stagger), with its names and icons
  const champs = Object.entries(breakers ?? {}).filter(([, d]) => d?.enumValue >= 1 && d.enumValue <= 3 && d.displayProperties?.icon).sort(([, a], [, b]) => a.enumValue - b.enumValue);
  return (
    <div className="im-filters" onClick={(e) => e.stopPropagation()}>
      <div className="im-fgroup">{ELEMENT_CHIPS.map(([e, name]) => chip(`e:${e}`, name, elementIcons?.[e] ? <Pic src={elementIcons[e]} width={16} height={16} /> : <i className={`im-el e${e}`} />, `${name} weapons`))}</div>
      <div className="im-fgroup three">{AMMO_CHIPS.map(([a, name, colour]) => chip(`a:${a}`, name, <svg viewBox="0 0 16 16" width="16" height="16"><path d="M3 4h10v8H3z" fill="none" stroke={colour} strokeWidth="1.4" /><path d="M6 6.5v3M8 6.5v3M10 6.5v3" stroke={colour} strokeWidth={a} /></svg>, `${name} ammo`))}</div>
      {champs.length > 0 && <div className="im-fgroup three">{champs.map(([hash, d]) => chip(`b:${hash}`, d.displayProperties.name, <Pic src={d.displayProperties.icon} width={16} height={16} />, `Stuns ${d.displayProperties.name} champions`))}</div>}
      <div className="im-fgroup five">{[1, 2, 3, 4, 5].map((t) => chip(`t:${t}`, '', <span className={`im-tier n${t} flat`}>{Array.from({ length: t }, (_, k) => <i key={k} />)}</span>, `Gear tier ${t}`))}</div>
      <div className="im-fgroup three">{FLAG_CHIPS.map(([k, name, tip]) => chip(k, name, <i className={`im-flag f-${k}`} />, tip))}</div>
      {chips.length > 0 && <button type="button" className="im-viewlink" onClick={() => setChips([])}>Clear the filters</button>}
    </div>
  );
}

/** The character's loadout slots from the game, numbered as there (empty ones as +); a click equips one. */
function GameLoadouts({ c, equip }: { c: HubChar; equip: (l: Loadout) => void }) {
  const h = useHub();
  const { data } = usePrivate(`loadouts:${c.id}`, (t) => loadLoadouts(h.player!, t, c.id));
  if (!data?.length) return null;
  return (
    <div className="im-gloads" onClick={(e) => e.stopPropagation()}>
      <div className="im-gloads-grid">
        {data.map((l) => l.items > 0 ? (
          <button key={l.index} type="button" className="im-gload" title={`${l.name}: click to equip it on your ${c.cls}`} onClick={() => equip(l)}>
            {l.color && <Pic src={l.color} className="im-gload-bg" />}{l.icon && <Pic src={l.icon} className="im-gload-icon" />}<b>{l.index + 1}</b>
          </button>
        ) : <span key={l.index} className="im-gload empty" title={`Slot ${l.index + 1} is empty: save a loadout there in the game`}>+</span>)}
      </div>
    </div>
  );
}

/** A slot's vault grid, a few rows high until opened (as Braytech folds it), with the bar that opens it under it. */
function VaultFold({ open, toggle, count, children }: { open: boolean; toggle: () => void; count: number; children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const check = () => setMore(el.scrollHeight > el.clientHeight + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  });
  return (
    <div className="im-fvault">
      <div ref={box} className={`im-fvault-box ${open ? 'open' : ''}`}>{children}</div>
      {(open || more) && (
        <button type="button" className={`im-fvault-more ${open ? 'open' : ''}`} title={open ? 'Show fewer rows' : `Show all ${count} in the vault`} onClick={(e) => { e.stopPropagation(); toggle(); }}>
          <svg viewBox="0 0 12 8" width="12" height="8"><path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
        </button>
      )}
    </div>
  );
}

// ---------------- compare: copies of an item (or every one of its type) side by side ----------------

/** True on a phone-sized window (the Item Manager then lays itself out like DIM's phone view). */
function useNarrow() {
  const q = '(max-width: 760px)';
  const [narrow, setNarrow] = useState(() => typeof matchMedia !== 'undefined' && matchMedia(q).matches);
  useEffect(() => {
    const m = matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return narrow;
}
